import { Col, Form, Row } from 'antd';
import React, { useEffect, useRef, useState } from 'react';

import GasPricesAPI from '../../api/gas-prices';
import { Button } from '../../components';
import Alert from '../../components/Alert';
import CompanyLookupSelect from '../../components/CompanyLookupSelect';
import CompanyRatesLookupSelect from '../../components/CompanyRatesLookupSelect';
import CompanyUsersLookupSelect from '../../components/CompanyUsersLookupSelect';
import FormItem from '../../components/Form/FormItem';
import LoadingSkeleton from '../../components/LoadingSkeleton';
import Radio from '../../components/Radio';
import SubmitCancelButtonGroup from '../../components/SubmitCancelButtonGroup';
import HorizontalSwitch from '../../components/Switch/HorizontalSwitch';
import Text from '../../components/Text';
import TextInput from '../../components/TextInput';
import NumericInput from '../../components/TextInput/NumericInput';
import TextWithExternalLink from '../../components/TextWithExternalLink';
import { EXTERNAL_LINKS } from '../../enum';
import {
  FIELD_REQUIRED_FORM_RULE,
  MIN_NUMBER_FORM_RULE,
  MINMAX_NUMBER_FORM_RULE,
} from '../../utils/constants';
import EditableUserAddressPanel from './EditableUserAddressPanel';
import InsuranceMonthlySelector from './InsuranceMonthlySelector';
import NewUserAddressForm from './NewUserAddressForm';
import StandardVehiclePanel from './StandardVehiclePanel';

const DisplayField = ({ value, formatter }) => (
  <Text variant="p" size="sm">
    {formatter ? formatter(value) : value != null ? String(value) : '—'}
  </Text>
);

const FavrInputsLoadingSection = ({ children, isLoading, error, onRetry, t }) => (
  <>
    {isLoading && <LoadingSkeleton style={{ height: 260, marginBottom: 16 }} />}
    {error && !isLoading && (
      <div style={{ marginBottom: 16 }}>
        <Alert
          fullWidth
          type="error"
          message={t('favrInputsError')}
          showIcon
          style={{ marginBottom: 8 }}
        />
        <Button size="xs" type="secondary" onClick={onRetry}>
          {t('retry')}
        </Button>
      </div>
    )}
    <div style={{ display: isLoading || error ? 'none' : 'block' }}>{children}</div>
  </>
);

const GenerateFavrRatesForm = props => {
  const {
    t,
    form,
    step,
    allowanceMode,
    companyId,
    userId,
    productId,
    retentionYears,
    inputsReady,
    isLoading,
    userZipCode,
    userDetails,
    productDetails,
    userState,
    insuranceRatesData,
    isLoadingUserDetails,
    isLoadingProductDetails,
    favrInputsLoading,
    favrInputsError,
    favrInputsReady,
    onRetryFavrInputs,
    onAllowanceModeChange,
    onValuesChange,
    onFinish,
    onCustomAddressChange,
    isNewUser,
    onNewUserModeChange,
    onNewUserEmailChange,
    onNewUserAddressChange,
    newUserAddress,
  } = props;

  const [gasPriceLoading, setGasPriceLoading] = useState(false);
  const [gasPriceError, setGasPriceError] = useState(null);
  const [gasPriceRetryCount, setGasPriceRetryCount] = useState(0);
  const latestUserZipCode = useRef(userZipCode);
  latestUserZipCode.current = userZipCode;

  useEffect(() => {
    let isActive = true;

    const isCurrentRequest = () => isActive && latestUserZipCode.current === userZipCode;

    form.setFields([{ name: 'fuelPrice', value: undefined, errors: [] }]);
    onValuesChange();
    setGasPriceError(null);
    setGasPriceLoading(!!userZipCode);

    const fetchGasPrice = async () => {
      if (!userZipCode) {
        setGasPriceError('userZipCodeNotAvailable');
        return;
      }

      try {
        const data = await new GasPricesAPI().fetchGasPrices(userZipCode);
        if (!isCurrentRequest()) return;

        const price = Number(data?.price);
        if (!Number.isFinite(price) || price <= 0) {
          setGasPriceError('noFuelPricesAvailable');
          return;
        }

        form.setFields([{ name: 'fuelPrice', value: price, errors: [] }]);
      } catch {
        if (isCurrentRequest()) {
          setGasPriceError('failedToFetchGasPrices');
        }
      } finally {
        if (isCurrentRequest()) {
          setGasPriceLoading(false);
        }
      }
    };

    fetchGasPrice();

    return () => {
      isActive = false;
    };
  }, [form, gasPriceRetryCount, onValuesChange, userZipCode]);

  const handleFetchGasPrice = () => {
    setGasPriceRetryCount(retryCount => retryCount + 1);
  };

  const countryFormatter = val =>
    val === 'US' ? t('unitedStates') : val === 'CA' ? t('canada') : val || '—';

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      onFinishFailed={errorInfo => console.error('Form Validation Failed:', errorInfo)}
      onValuesChange={onValuesChange}
      requiredMark
    >
      {/* Step 1: Context selector — always visible */}
      <FormItem name="companyId" label={t('Company')} rules={FIELD_REQUIRED_FORM_RULE()}>
        <CompanyLookupSelect t={t} disabled={isLoading} />
      </FormItem>

      <Row gutter={[16, 0]} hidden={!companyId}>
        <Col xs={24} md={12}>
          <FormItem
            name="productId"
            label={t('rate')}
            rules={FIELD_REQUIRED_FORM_RULE(!!companyId)}
          >
            <CompanyRatesLookupSelect t={t} companyId={companyId} disabled={isLoading} />
          </FormItem>
        </Col>

        <Col xs={24} md={12}>
          {isLoadingProductDetails ? (
            <LoadingSkeleton style={{ height: 152, marginBottom: 16 }} />
          ) : productId && productDetails ? (
            <StandardVehiclePanel t={t} productDetails={productDetails} />
          ) : null}
        </Col>
      </Row>

      <Row gutter={[16, 0]} hidden={!productId}>
        <Col xs={24} md={12}>
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {productId && (
              <>
                <FormItem name="userType" initialValue="existing">
                  <Radio.Group
                    disabled={isLoading}
                    onChange={e => onNewUserModeChange(e.target.value)}
                  >
                    <Radio value="existing">{t('existingUser')}</Radio>
                    <Radio value="new">{t('newUser')}</Radio>
                  </Radio.Group>
                </FormItem>

                {!isNewUser ? (
                  <>
                    <FormItem
                      name="userId"
                      label={t('user')}
                      rules={FIELD_REQUIRED_FORM_RULE(!!productId && !isNewUser)}
                    >
                      <CompanyUsersLookupSelect t={t} companyId={companyId} disabled={isLoading} />
                    </FormItem>
                  </>
                ) : (
                  <>
                    <FormItem
                      name="newUserEmail"
                      label={t('email')}
                      rules={[
                        ...FIELD_REQUIRED_FORM_RULE(isNewUser),
                        {
                          type: 'email',
                          message: t('validEmail'),
                        },
                      ]}
                    >
                      <TextInput
                        disabled={isLoading}
                        onChange={e => onNewUserEmailChange(e.target.value)}
                      />
                    </FormItem>
                  </>
                )}
              </>
            )}
          </div>
        </Col>

        <Col xs={24} md={12}>
          {!isNewUser ? (
            isLoadingUserDetails && userId ? (
              <LoadingSkeleton style={{ height: 152, marginBottom: 16 }} />
            ) : userId && userDetails ? (
              <EditableUserAddressPanel
                t={t}
                userDetails={userDetails}
                onAddressChange={onCustomAddressChange}
                isLoading={isLoading}
              />
            ) : null
          ) : (
            <NewUserAddressForm
              t={t}
              form={form}
              onAddressChange={onNewUserAddressChange}
              isLoading={isLoading}
              addressValues={newUserAddress}
            />
          )}
        </Col>
      </Row>

      {/* Step 2: FAVR inputs — shown only after Step 1 is complete */}
      {step === 'inputs' && inputsReady && (
        <>
          <div>
            <>
              <FormItem className="no-margin-bottom" style={{ marginTop: 16 }}>
                <HorizontalSwitch label={t('allowanceMode')} onChange={onAllowanceModeChange} />
              </FormItem>

              <section>
                {/* Country — read-only, value derived from product */}
                <FormItem name="country" label={t('country')} rules={FIELD_REQUIRED_FORM_RULE()}>
                  <DisplayField formatter={countryFormatter} />
                </FormItem>

                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12} style={{ display: allowanceMode ? 'block' : 'none' }}>
                    <FormItem
                      name="fixedRate"
                      label={t('fixedRate')}
                      rules={FIELD_REQUIRED_FORM_RULE(allowanceMode)}
                    >
                      <NumericInput disabled={isLoading} />
                    </FormItem>
                  </Col>

                  <Col xs={24} md={12}>
                    <FormItem
                      name="businessUsePercentage"
                      label={t('businessUse%')}
                      rules={[...FIELD_REQUIRED_FORM_RULE(), ...MINMAX_NUMBER_FORM_RULE(0, 75)]}
                    >
                      <NumericInput suffix="%" min={0} max={75} disabled={isLoading} />
                    </FormItem>
                  </Col>

                  <Col xs={24} md={12} style={{ display: allowanceMode ? 'none' : 'block' }}>
                    <FormItem
                      name="insuranceMonthly"
                      label={t('insuranceMonthly')}
                      rules={[
                        ...FIELD_REQUIRED_FORM_RULE(!allowanceMode),
                        ...MIN_NUMBER_FORM_RULE(1),
                      ]}
                    >
                      <InsuranceMonthlySelector
                        userState={userState}
                        insuranceRatesData={insuranceRatesData}
                        disabled={isLoading}
                        t={t}
                      />
                    </FormItem>
                  </Col>
                </Row>

                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <FormItem
                      name="retentionYears"
                      label={t('retentionYears')}
                      rules={[...FIELD_REQUIRED_FORM_RULE(), ...MIN_NUMBER_FORM_RULE(1)]}
                    >
                      <NumericInput min={1} disabled={isLoading || allowanceMode} />
                    </FormItem>
                  </Col>

                  <Col xs={24} md={12}>
                    <FormItem
                      name="retentionMiles"
                      label={t('retentionMiles')}
                      rules={[...FIELD_REQUIRED_FORM_RULE(), ...MIN_NUMBER_FORM_RULE(1)]}
                    >
                      <NumericInput min={1} disabled={isLoading || allowanceMode} />
                    </FormItem>
                  </Col>
                </Row>

                <FavrInputsLoadingSection
                  isLoading={favrInputsLoading}
                  error={favrInputsError}
                  onRetry={onRetryFavrInputs}
                  t={t}
                >
                  <div style={{ marginBottom: 24 }}>
                    <Form.List
                      name="costsPerYear"
                      rules={[
                        ...FIELD_REQUIRED_FORM_RULE(),
                        ...MIN_NUMBER_FORM_RULE(retentionYears),
                      ]}
                    >
                      {fields => (
                        <div>
                          {fields.length > 0 && (
                            <FormItem className="no-margin-bottom" label={t('costsPerYear')} />
                          )}

                          {fields.map((field, idx) => (
                            <div
                              key={field.key}
                              style={{
                                background: idx % 2 === 0 ? '#fafbfc' : '#eff2f7',
                                borderBottom:
                                  idx !== fields.length - 1 ? '1px solid #e5e5e5' : undefined,
                                padding: 12,
                              }}
                            >
                              <Row gutter={[16, 16]} align="middle">
                                <Col xs={24}>
                                  <Text variant="strong" size="xs">{`${t('year')} ${
                                    idx + 1
                                  }`}</Text>
                                </Col>

                                <Col xs={24} sm={12} md={6}>
                                  <FormItem
                                    {...field}
                                    name={[field.name, 'repairs']}
                                    label={t('repairs')}
                                    rules={[
                                      ...FIELD_REQUIRED_FORM_RULE(),
                                      ...MIN_NUMBER_FORM_RULE(0),
                                    ]}
                                  >
                                    <NumericInput disabled={isLoading} />
                                  </FormItem>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                  <FormItem
                                    {...field}
                                    name={[field.name, 'maintenance']}
                                    label={t('maintenance')}
                                    rules={[
                                      ...FIELD_REQUIRED_FORM_RULE(),
                                      ...MIN_NUMBER_FORM_RULE(0),
                                    ]}
                                  >
                                    <NumericInput disabled={isLoading} />
                                  </FormItem>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                  <FormItem
                                    {...field}
                                    name={[field.name, 'taxesAndFees']}
                                    label={t('taxesAndFees')}
                                    rules={[
                                      ...FIELD_REQUIRED_FORM_RULE(),
                                      ...MIN_NUMBER_FORM_RULE(0),
                                    ]}
                                  >
                                    <NumericInput disabled={isLoading} />
                                  </FormItem>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                  <FormItem
                                    {...field}
                                    name={[field.name, 'depreciation']}
                                    label={t('depreciation')}
                                    rules={[
                                      ...FIELD_REQUIRED_FORM_RULE(),
                                      ...MIN_NUMBER_FORM_RULE(0),
                                    ]}
                                  >
                                    <NumericInput disabled={isLoading} />
                                  </FormItem>
                                </Col>
                              </Row>
                            </div>
                          ))}
                        </div>
                      )}
                    </Form.List>
                  </div>

                  <Row gutter={[16, 8]}>
                    <Col xs={24} md={8}>
                      <FormItem
                        name="cashPrice"
                        label={t('cashPrice')}
                        rules={[...FIELD_REQUIRED_FORM_RULE(), ...MIN_NUMBER_FORM_RULE(1)]}
                      >
                        <NumericInput disabled />
                      </FormItem>
                    </Col>

                    <Col xs={24} md={8}>
                      <FormItem
                        name="tires"
                        label={t('tires')}
                        rules={[...FIELD_REQUIRED_FORM_RULE(), ...MIN_NUMBER_FORM_RULE(1)]}
                      >
                        <NumericInput disabled />
                      </FormItem>
                    </Col>

                    <Col xs={24} md={8}>
                      <FormItem
                        name="fuelType"
                        label={t('fuelType')}
                        getValueProps={value => ({ value: value ? t(value) : value })}
                        rules={FIELD_REQUIRED_FORM_RULE()}
                      >
                        <TextInput disabled />
                      </FormItem>
                    </Col>
                  </Row>

                  <Row gutter={[16, 8]}>
                    <Col xs={24} md={8}>
                      <FormItem
                        name="fuelMpgCity"
                        label={t('fuelMpgCity')}
                        rules={[...FIELD_REQUIRED_FORM_RULE(), ...MINMAX_NUMBER_FORM_RULE(1, 100)]}
                      >
                        <NumericInput disabled />
                      </FormItem>
                    </Col>

                    <Col xs={24} md={8}>
                      <FormItem
                        name="fuelMpgHighway"
                        label={t('fuelMpgHighway')}
                        rules={[...FIELD_REQUIRED_FORM_RULE(), ...MINMAX_NUMBER_FORM_RULE(1, 100)]}
                      >
                        <NumericInput disabled />
                      </FormItem>
                    </Col>
                  </Row>
                </FavrInputsLoadingSection>

                <Row gutter={[16, 8]} align="middle">
                  <Col xs={24} md={12}>
                    <FormItem
                      name="fuelPrice"
                      label={t('fuelPrice')}
                      rules={[...FIELD_REQUIRED_FORM_RULE(), ...MIN_NUMBER_FORM_RULE(1)]}
                    >
                      <NumericInput min={1} disabled={isLoading || gasPriceLoading} />
                    </FormItem>
                  </Col>

                  <Col xs={24} md={6}>
                    <Button
                      block
                      size="xs"
                      type="secondary"
                      onClick={handleFetchGasPrice}
                      loading={gasPriceLoading}
                      disabled={isLoading}
                      style={{ whiteSpace: 'normal', height: 'auto' }}
                    >
                      {t('getFuelPriceFromDB')}
                    </Button>
                  </Col>

                  {userZipCode && (
                    <Col xs={24} md={6}>
                      <TextWithExternalLink
                        text={t('openGasBuddyPage')}
                        linkProps={{
                          href: EXTERNAL_LINKS.GASBUDDY(userZipCode),
                        }}
                      />
                    </Col>
                  )}

                  <Col xs={24} md={12}>
                    <FormItem
                      name="highwayDrivingPercentage"
                      label={t('highwayDrivingPercentage')}
                      rules={[...FIELD_REQUIRED_FORM_RULE(), ...MINMAX_NUMBER_FORM_RULE(1, 100)]}
                    >
                      <NumericInput suffix="%" min={1} max={100} disabled={isLoading} />
                    </FormItem>
                  </Col>
                </Row>

                {gasPriceError && (
                  <Alert
                    fullWidth
                    message={t(gasPriceError)}
                    type="error"
                    showIcon
                    closable
                    onClose={() => setGasPriceError(null)}
                    style={{ marginBottom: 16 }}
                  />
                )}
              </section>

              <SubmitCancelButtonGroup
                isForm
                hideCancel
                loading={isLoading}
                disabled={
                  isLoading ||
                  gasPriceLoading ||
                  favrInputsLoading ||
                  !favrInputsReady ||
                  !!favrInputsError
                }
                submitText={t('generateRate')}
              />
            </>
          </div>
        </>
      )}
    </Form>
  );
};

export default GenerateFavrRatesForm;
